import { useState } from "preact/hooks"
import useSWR from "swr"
import { SettingsLayout } from "./layout"
import { Section } from "./index"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { api_fetch, api_fetch_throws } from "src/api/client.tsx"
import { toast } from "@/components/ui/use-toast"
import { format, parseISO } from "date-fns"
import { usePageTitle } from "@/utils"
import { UserState } from "@/auth/user"
import {
  type AgentRun,
  format_action_payload_json,
  format_agent_run_deduction_amount,
} from "@/api/agent_run.ts"
import { openDeductionDetailModal } from "@/deduction/deduction_detail_modal_state"
import { parse_dollar_override } from "./dollar_override"
import type { AgentEligibility, AgentEligibilityCriterion, RunAgentNowResponse } from "@/api/agent.ts"

interface AgentConfig {
  id: string
  enabled: boolean
  dry_run: boolean
  max_deductions_per_day: number
  sources: string[]
  allowed_actions: string[]
  model: string
  match_promotions: boolean
  validate_task_min_amount?: number | null
  validate_task_always_amount?: number | null
  hold_amount_validations?: boolean
  created_at: string
  updated_at: string
  last_batch_at?: string | null
  last_batch_eligible_count?: number | null
  last_batch_enqueued_count?: number | null
  last_batch_message?: string | null
}

// Actions gated by the per-org whitelist below (executed without human approval only when checked).
const GATED_ACTIONS = ["file_dispute", "request_backup", "assign_task", "add_note"]
// Low-risk, internal-only actions that always run (still suppressed in dry-run). Shown for transparency.
const ALWAYS_ON_ACTIONS = ["categorize", "update_status", "update_fields", "complete_task"]
const ALL_SOURCES = ["unfi", "kehe"]

const PRESET_MODELS = [
  { label: "Gemini 3.8 Flash (Google — default)", value: "google/gemini-3.8-flash" },
  { label: "Gemini 3.6 Flash (Google — previous default)", value: "google/gemini-3.6-flash" },
  { label: "Claude Sonnet 5 (Anthropic)", value: "anthropic/claude-sonnet-5" },
  { label: "Claude Opus 4.8 (Anthropic — highest quality)", value: "anthropic/claude-opus-4-8" },
  { label: "Claude Sonnet 4.6 (Anthropic — previous gen)", value: "anthropic/claude-sonnet-4-6" },
  { label: "Gemini 3.1 Pro Preview (Google)", value: "google/gemini-3.1-pro-preview" },
  { label: "GPT-5.5 (OpenAI)", value: "openai/gpt-5.5" },
  { label: "Auto — OpenRouter picks best model", value: "openrouter/auto" },
]

export function SettingsAgentPage() {
  usePageTitle("BigWiz")
  return (
    <SettingsLayout>
      <AgentConfigSection />
      <AgentRunsSection />
    </SettingsLayout>
  )
}

function AgentConfigSection() {
  const user = UserState.use()
  const is_system_admin = user?.role === "system_admin"
  const { data: config, mutate } = useSWR<AgentConfig | null>("/agent/config", () =>
    api_fetch_throws<AgentConfig | null>("/agent/config")
  )
  const { data: eligibility, mutate: mutate_eligibility } = useSWR<AgentEligibility>("/agent/eligibility", () =>
    api_fetch_throws<AgentEligibility>("/agent/eligibility")
  )
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [form, setForm] = useState<Partial<AgentConfig> | null>(null)
  const [criteria_open, set_criteria_open] = useState(false)

  const current = form ?? config ?? {
    enabled: false,
    dry_run: true,
    max_deductions_per_day: 10,
    sources: ["unfi", "kehe"],
    allowed_actions: ["add_note", "skip"],
    model: "google/gemini-3.8-flash",
    match_promotions: true,
    validate_task_min_amount: null,
    validate_task_always_amount: null,
    hold_amount_validations: false,
  }

  function toggle_action(action: string) {
    const actions = (current.allowed_actions ?? [])
    const next = actions.includes(action) ? actions.filter(a => a !== action) : [...actions, action]
    setForm({ ...current, allowed_actions: next })
  }

  function toggle_source(source: string) {
    const sources = (current.sources ?? [])
    const next = sources.includes(source) ? sources.filter(s => s !== source) : [...sources, source]
    setForm({ ...current, sources: next })
  }

  async function save_config() {
    const body: Record<string, unknown> = {
      enabled: current.enabled,
      dry_run: current.dry_run,
      max_deductions_per_day: current.max_deductions_per_day,
      sources: current.sources,
      allowed_actions: current.allowed_actions,
      match_promotions: current.match_promotions,
      // 0 clears the override back to the default gate (server treats <= 0 as "no override").
      validate_task_min_amount: current.validate_task_min_amount ?? 0,
      validate_task_always_amount: current.validate_task_always_amount ?? 0,
      hold_amount_validations: current.hold_amount_validations ?? false,
    }
    if (is_system_admin) {
      body.model = current.model
    }
    const saved = await api_fetch_throws<AgentConfig>("/agent/config", {
      method: "POST",
      body,
    })
    mutate(saved)
    void mutate_eligibility()
    setForm(null)
    return saved
  }

  async function save() {
    setSaving(true)
    try {
      await save_config()
    } finally {
      setSaving(false)
    }
  }

  async function run_now() {
    setRunning(true)
    try {
      // Always save current settings first so the job uses the correct max_deductions_per_day.
      if (form !== null) await save_config()
      const result = await api_fetch<RunAgentNowResponse>("/agent/run-now", { method: "POST", body: {} })
      if (result.ok) {
        const body = result.value.data
        const low_yield = body.will_enqueue === 0
        toast({
          title: "BigWiz queued",
          description: body.message,
          variant: low_yield ? "destructive" : "default",
        })
        window.setTimeout(() => void mutate_eligibility(), 3500)
        void mutate()
      } else {
        toast({
          title: "Failed to enqueue BigWiz",
          description: result.error.message,
          variant: "destructive",
        })
      }
    } finally {
      setRunning(false)
    }
  }

  return (
    <Section title="BigWiz Configuration" class="bg-white rounded-lg shadow-sm p-6">
      <p class="text-sm text-gray-500 mb-4">
        When enabled, BigWiz runs on your saved UNFI / KeHE sources and picks the best next action from each deduction's
        context, notes, emails, and history. All runs are logged below.
      </p>

      <div class="mb-6 rounded-md border border-gray-200 bg-gray-50/80 p-4 max-w-2xl space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="text-sm font-medium text-gray-800">Eligible deductions</p>
          <button
            type="button"
            onClick={() => set_criteria_open(v => !v)}
            class="text-xs text-blue-600 hover:underline"
          >
            {criteria_open ? "Hide checklist" : "What counts as eligible?"}
          </button>
        </div>
        <p class="text-sm text-gray-800">
          <span class="font-semibold tabular-nums">{eligibility?.eligible_count ?? "—"}</span> deductions match your filters.{" "}
          One <span class="font-medium">Run now</span> queues up to{" "}
          <span class="font-semibold tabular-nums">{eligibility?.will_enqueue_run_now ?? "—"}</span>{" "}
          of them in a single batch, capped by <span class="font-medium">Max deductions per day</span> below — that setting is the batch size for Run now
          (not a limit of one deduction per click). This applies whether or not daily automatic runs are enabled.
        </p>
        <p class="text-xs text-gray-500">
          Next <span class="font-medium">scheduled</span> daily run would queue up to{" "}
          <span class="font-medium tabular-nums">{eligibility?.will_enqueue ?? "—"}</span>
          {" "}
          (<span class="tabular-nums">{eligibility?.daily_remaining ?? "—"}</span> slots left today after successful runs).
        </p>
        {criteria_open && (
          <ul class="mt-2 space-y-2 text-sm text-gray-700 border-t border-gray-200 pt-3">
            {(eligibility?.criteria ?? []).map((c: AgentEligibilityCriterion) => (
              <li key={c.title}>
                <span class="font-medium text-gray-900">{c.title}</span>
                <span class="text-gray-600"> — {c.detail}</span>
              </li>
            ))}
          </ul>
        )}
        {eligibility?.last_batch?.at != null && eligibility.last_batch.at !== "" && (
          <p class="text-xs text-gray-600 border-t border-gray-200 pt-3">
            <span class="font-medium text-gray-800">Last batch </span>
            {format(parseISO(eligibility.last_batch.at), "MMM d, yyyy 'at' h:mm a")}
            {eligibility.last_batch.enqueued_count != null ? ` — queued ${eligibility.last_batch.enqueued_count}.` : "."}
          </p>
        )}
        {eligibility?.last_batch?.message && (
          <p class="text-xs text-gray-600">{eligibility.last_batch.message}</p>
        )}
      </div>

      <div class="space-y-5 max-w-lg">
        <div class="flex items-center gap-3">
          <input
            type="checkbox"
            id="enabled"
            checked={current.enabled}
            onChange={e => setForm({ ...current, enabled: (e.target as HTMLInputElement).checked })}
            class="h-4 w-4 rounded border-gray-300 text-blue-600"
          />
          <Label for="enabled">Enable agent to run daily</Label>
        </div>

        <div class="flex items-center gap-3">
          <input
            type="checkbox"
            id="dry_run"
            checked={current.dry_run}
            onChange={e => setForm({ ...current, dry_run: (e.target as HTMLInputElement).checked })}
            class="h-4 w-4 rounded border-gray-300 text-blue-600"
          />
          <div>
            <Label for="dry_run">Dry run</Label>
            <p class="text-xs text-gray-400">Agent decides and logs but does not execute any actions.</p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <input
            type="checkbox"
            id="match_promotions"
            checked={current.match_promotions ?? true}
            onChange={e => setForm({ ...current, match_promotions: (e.target as HTMLInputElement).checked })}
            class="h-4 w-4 rounded border-gray-300 text-blue-600"
          />
          <div>
            <Label for="match_promotions">Match promotions & create splits</Label>
            <p class="text-xs text-gray-400">
              Link deductions to loaded promotions and create GL coding splits (splitting across promotions when
              needed). Only applies when promotions are loaded in the system. Deductions already coded to a GL
              account but not yet linked to a promotion are re-split, keeping that account; deductions already
              matched to a promotion are left alone.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <Checkbox
            id="hold_amount_validations"
            checked={current.hold_amount_validations ?? false}
            onCheckedChange={(checked: boolean | "indeterminate") => setForm({ ...current, hold_amount_validations: checked === true })}
          />
          <div>
            <Label for="hold_amount_validations">Don't validate promo charges on their size alone</Label>
            <p class="text-xs text-gray-400">
              Hold small promotion charges for review unless a promotion covers them, instead of validating them because
              they're under the validate-task amount.
            </p>
          </div>
        </div>

        <div>
          <Label>Sources</Label>
          <div class="mt-1 flex gap-4">
            {ALL_SOURCES.map(s => (
              <label key={s} class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(current.sources ?? []).includes(s)}
                  onChange={() => toggle_source(s)}
                  class="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span class="text-sm capitalize">{s.toUpperCase()}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>Allowed actions (executed without human approval)</Label>
          <div class="mt-1 grid grid-cols-2 gap-2">
            {GATED_ACTIONS.map(a => (
              <label key={a} class="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(current.allowed_actions ?? []).includes(a)}
                  onChange={() => toggle_action(a)}
                  class="h-4 w-4 rounded border-gray-300 text-blue-600"
                />
                <span class="text-sm font-mono">{a}</span>
              </label>
            ))}
          </div>
          <p class="mt-3 text-xs text-gray-500">Always on (low-risk, internal only — still skipped during dry run):</p>
          <div class="mt-1 grid grid-cols-2 gap-2">
            {ALWAYS_ON_ACTIONS.map(a => (
              <label key={a} class="flex items-center gap-2 text-gray-500">
                <input type="checkbox" checked disabled class="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <span class="text-sm font-mono">{a}</span>
                <span class="text-[10px] uppercase tracking-wide text-gray-400">always on</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label for="max_per_day">Max deductions per day</Label>
          <Input
            id="max_per_day"
            type="number"
            min={1}
            max={500}
            value={current.max_deductions_per_day}
            onInput={e => setForm({ ...current, max_deductions_per_day: parseInt((e.target as HTMLInputElement).value) || 10 })}
            class="mt-1 w-32"
          />
        </div>

        <div>
          <Label for="validate_task_min_amount">Minimum amount for "validate" tasks ($)</Label>
          <Input
            id="validate_task_min_amount"
            type="number"
            min={0}
            step={100}
            value={current.validate_task_min_amount ?? ""}
            onInput={e => setForm({ ...current, validate_task_min_amount: parse_dollar_override((e.target as HTMLInputElement).value) })}
            class="mt-1 w-32"
          />
          <p class="text-xs text-gray-400">
            BigWiz won't suggest a "validate" task for deductions below this absolute amount — it validates or
            skips instead. Leave blank for the default (~$1,000).
          </p>
        </div>

        <div>
          <Label for="validate_task_always_amount">Always send retailer promos to sales at or above ($)</Label>
          <Input
            id="validate_task_always_amount"
            type="number"
            min={0}
            step={1000}
            value={current.validate_task_always_amount ?? ""}
            onInput={e => setForm({ ...current, validate_task_always_amount: parse_dollar_override((e.target as HTMLInputElement).value) })}
            class="mt-1 w-32"
          />
          <p class="text-xs text-gray-400">
            Every retailer promotion, ad, scan or EDLP charge of this absolute amount or more gets a "validate" task
            for the salesperson who owns that retailer, even when it matches the promo calendar. Leave blank for the
            default (BigWiz uses judgment, and assigns from ~$10,000).
          </p>
        </div>

        {is_system_admin && (
          <div>
            <Label for="model">Model (OpenRouter)</Label>
            <Select
              value={current.model ?? PRESET_MODELS[0].value}
              onValueChange={(v: string) => setForm({ ...current, model: v })}
            >
              <SelectTrigger id="model" class="w-full mt-1">
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                {PRESET_MODELS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div class="flex gap-3 pt-2">
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button variant="outline" onClick={run_now} disabled={running}>
            {running ? "Enqueueing…" : "Run now"}
          </Button>
        </div>
      </div>
    </Section>
  )
}

function AgentRunsSection() {
  const user = UserState.use()
  const is_system_admin = user?.role === "system_admin"
  const { data: runs, mutate } = useSWR<AgentRun[]>("/agent/runs", () =>
    api_fetch_throws<AgentRun[]>("/agent/runs?limit=100")
  )

  async function rate(run_id: string, rating: "thumbs_up" | "thumbs_down") {
    await api_fetch_throws(`/agent/runs/${run_id}/rate`, {
      method: "POST",
      body: { rating },
    })
    mutate()
  }

  return (
    <Section title="Recent BigWiz Runs" class="bg-white rounded-lg shadow-sm p-6">
      {!runs || runs.length === 0 ? (
        <p class="text-sm text-gray-400">No runs yet.</p>
      ) : (
        <div class="space-y-3">
          {runs.map(run => (
            <AgentRunRow
              key={run.id}
              run={run}
              onRate={rate}
              is_system_admin={is_system_admin}
              on_mutate={() => void mutate()}
            />
          ))}
        </div>
      )}
    </Section>
  )
}

interface AgentRunRowProps {
  run: AgentRun
  onRate: (id: string, rating: "thumbs_up" | "thumbs_down") => void
  is_system_admin: boolean
  on_mutate: () => void
}

function AgentRunRow({ run, onRate, is_system_admin, on_mutate }: AgentRunRowProps) {
  const [expanded, setExpanded] = useState(false)
  const [executing, setExecuting] = useState(false)

  const action_color: Record<string, string> = {
    file_dispute: "bg-green-100 text-green-800",
    update_status: "bg-blue-100 text-blue-800",
    update_fields: "bg-cyan-100 text-cyan-800",
    request_backup: "bg-yellow-100 text-yellow-800",
    assign_task: "bg-purple-100 text-purple-800",
    add_note: "bg-gray-100 text-gray-700",
    categorize: "bg-teal-100 text-teal-800",
    complete_task: "bg-indigo-100 text-indigo-800",
    skip: "bg-gray-50 text-gray-400",
    error: "bg-red-100 text-red-700",
  }

  // `action_taken` is a comma-joined list of the actions taken this run.
  const actions = run.action_taken.split(",").map(a => a.trim()).filter(Boolean)

  const desc_preview =
    run.category_description && run.category_description.length > 100
      ? `${run.category_description.slice(0, 97)}…`
      : run.category_description

  const can_show_dry_payload = is_system_admin && run.dry_run
  const can_execute_dry =
    is_system_admin && run.dry_run && run.success && run.action_taken !== "error" && !run.manual_decision_executed_at

  async function execute_stored_decision() {
    setExecuting(true)
    try {
      const res = await api_fetch(`/agent/runs/${run.id}/execute-stored-decision`, {
        method: "POST",
        body: {},
      })
      if (res.ok) {
        toast({
          title: "Action executed",
          description: "The dry-run decision has been applied. Deduction state may have changed since the run.",
        })
        on_mutate()
      } else {
        toast({
          title: "Execute failed",
          description: res.error?.message ?? "Unknown error",
          variant: "destructive",
        })
      }
    } finally {
      setExecuting(false)
    }
  }

  return (
    <div class={`border rounded-lg p-3 ${run.success ? "border-gray-200" : "border-red-200 bg-red-50"}`}>
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-1 shrink-0">
            {actions.map(a => (
              <span key={a} class={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${action_color[a] ?? "bg-gray-100 text-gray-600"}`}>
                {a}
              </span>
            ))}
          </div>
          {run.dry_run && (
            <span class="shrink-0 inline-flex items-center rounded px-2 py-0.5 text-xs bg-orange-50 text-orange-600 border border-orange-200">
              dry run
            </span>
          )}
          <button
            type="button"
            onClick={(e: MouseEvent) => {
              e.preventDefault()
              e.stopPropagation()
              openDeductionDetailModal(run.deduction_id)
            }}
            class="text-xs text-blue-600 hover:underline truncate"
          >
            {run.invoice_number || run.deduction_id.slice(0, 8)}
          </button>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <span class="text-xs text-gray-400">
            {format(parseISO(run.created_at), "MMM d HH:mm")}
          </span>
          <button
            onClick={() => onRate(run.id, "thumbs_up")}
            class={`text-base leading-none ${run.rating === "thumbs_up" ? "text-green-600" : "text-gray-300 hover:text-gray-500"}`}
            title="Good decision"
          >
            👍
          </button>
          <button
            onClick={() => onRate(run.id, "thumbs_down")}
            class={`text-base leading-none ${run.rating === "thumbs_down" ? "text-red-500" : "text-gray-300 hover:text-gray-500"}`}
            title="Bad decision"
          >
            👎
          </button>
          <button
            onClick={() => setExpanded(v => !v)}
            class="text-xs text-gray-400 hover:text-gray-600"
          >
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {run.error && (
        <p class="mt-2 text-xs text-red-600 font-mono">{run.error}</p>
      )}

      <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
        <span class="font-medium text-gray-800">{format_agent_run_deduction_amount(run.deduction_amount_usd)}</span>
        <span class="text-gray-300">·</span>
        <span class="truncate max-w-[14rem]" title={run.category_name}>
          {run.category_name}
        </span>
        {desc_preview && (
          <>
            <span class="text-gray-300">·</span>
            <span class="text-gray-500 truncate max-w-[20rem]" title={run.category_description ?? undefined}>
              {desc_preview}
            </span>
          </>
        )}
        <span class="text-gray-300">·</span>
        <span class="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
          {run.status_value.replaceAll("_", " ")}
        </span>
        {run.manual_decision_executed_at && (
          <>
            <span class="text-gray-300">·</span>
            <span class="text-emerald-700">Manually executed</span>
          </>
        )}
      </div>

      {expanded && (
        <div class="mt-3 space-y-2 text-xs text-gray-600">
          {run.reasoning && (
            <div>
              <p class="font-medium text-gray-700 mb-1">Reasoning</p>
              <p class="whitespace-pre-wrap">{run.reasoning}</p>
            </div>
          )}
          {can_show_dry_payload && (
            <div>
              <p class="font-medium text-gray-700 mb-1">Dry run — stored action (JSON)</p>
              <pre class="whitespace-pre-wrap break-words rounded bg-gray-50 border border-gray-100 p-2 text-[11px] leading-snug max-h-64 overflow-y-auto">
                {format_action_payload_json(run.action_payload)}
              </pre>
              {can_execute_dry && (
                <p class="mt-2 text-[11px] text-amber-700">
                  The deduction may have changed since this run. Only execute if you accept that risk.
                </p>
              )}
              {can_execute_dry && (
                <div class="mt-2">
                  <Button type="button" size="sm" variant="outline" disabled={executing} onClick={() => void execute_stored_decision()}>
                    {executing ? "Executing…" : "Execute this action"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
